import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Input,
  IconButton,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useWaapAspect,
  useUpdateWaapAspect,
  WAAP_AREAS,
  type WaapOrigin,
  type WaapOriginEntry,
  type WaapReqHeader,
} from '@/api/waap';
import { errMessage } from '@/pages/services/waap/waap-util';
import styles from './OriginTab.module.css';

const PROTOCOLS = ['https://', 'http://'];

// Upstream load-balancing algorithms — values are the vendor's /site
// upstream.load_balance enum; labels mirror the portal dropdown.
const LOAD_BALANCE_OPTIONS = [
  { value: 'ip_hash', label: 'IP Hash' },
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'least_conn', label: 'Least Connections' },
];

const MAX_ORIGINS = 5;

const NEW_ORIGIN: WaapOriginEntry = { protocol: 'https://', origin: '', weight: 1 };
const NEW_HEADER: WaapReqHeader = { key: '', value: '', enabled: true };

// The fixed "Host" header (vendor enable_host / host_value) is always the first
// request_headers row — locked key, not deletable.
const HOST_HEADER: WaapReqHeader = { key: 'Host', value: '$http_host', enabled: true };

const DEFAULTS: WaapOrigin = {
  origins: [{ ...NEW_ORIGIN }],
  load_balance: 'round_robin',
  enable_failover: true,
  keep_alive: true,
  http2: true,
  redirect_http_to_https: true,
  request_headers: [{ ...HOST_HEADER }],
};

/**
 * Origin — the website-detail Origin tab. Origin servers (1-5, with weight),
 * load balancing, keep-alive, HTTP/2, redirect, and custom request headers.
 * Persisted via the private portal across /cdn/domain + /site (one backend PUT).
 * Website fixed by the detail route (`:domainId`).
 */
export function OriginTab() {
  const { domainId } = useParams<{ domainId: string }>();
  const { show } = useToast();

  const { data, isLoading } = useWaapAspect<WaapOrigin>(domainId, WAAP_AREAS.origin);
  const [form, setForm] = useState<WaapOrigin>(DEFAULTS);
  useEffect(() => {
    if (data) {
      setForm({
        ...DEFAULTS,
        ...data,
        origins: data.origins?.length ? data.origins : [{ ...NEW_ORIGIN }],
        request_headers: data.request_headers?.length ? data.request_headers : [{ ...HOST_HEADER }],
      });
    }
  }, [data]);

  const update = useUpdateWaapAspect<WaapOrigin>(domainId ?? '', WAAP_AREAS.origin, {
    onSuccess: () => show({ tone: 'success', title: 'Origin saved' }),
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  const set = <K extends keyof WaapOrigin>(k: K, v: WaapOrigin[K]) => setForm((f) => ({ ...f, [k]: v }));

  const setOrigin = (i: number, patch: Partial<WaapOriginEntry>) =>
    set('origins', form.origins.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const addOrigin = () => set('origins', [...form.origins, { ...NEW_ORIGIN }]);
  const removeOrigin = (i: number) => set('origins', form.origins.filter((_, idx) => idx !== i));

  const setHeader = (i: number, patch: Partial<WaapReqHeader>) =>
    set('request_headers', form.request_headers.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const addHeader = () => set('request_headers', [...form.request_headers, { ...NEW_HEADER }]);
  const removeHeader = (i: number) => set('request_headers', form.request_headers.filter((_, idx) => idx !== i));

  const save = () => {
    update.mutate({
      ...form,
      origins: form.origins
        .map((o) => ({ ...o, origin: o.origin.trim(), weight: Number(o.weight) || 1 }))
        .filter((o) => o.origin),
      request_headers: form.request_headers.map((h) => ({ ...h, key: h.key.trim() })).filter((h) => h.key),
    });
  };

  const validOrigins = form.origins.filter((o) => o.origin.trim()).length > 0;

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  return (
    <div className={styles.stack}>
      <Card>
        <CardHeader
          actions={
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Plus size={14} />}
              disabled={form.origins.length >= MAX_ORIGINS}
              onClick={addOrigin}
            >
              Add Origin URL
            </Button>
          }
        >
          <CardTitle>Origin URL</CardTitle>
          <CardDescription>
            Add 1–5 origin servers (domain or IPv4 — IPv6 not supported). Weight sets how
            traffic is split across origins when you have more than one.
          </CardDescription>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          {/* Column labels so it's clear what each field is (protocol / origin
              server address / traffic weight). */}
          <div className={styles.colHeadRow}>
            <span>Protocol</span>
            <span>Origin server</span>
            <span>Weight</span>
          </div>
          {form.origins.map((o, i) => (
            <div key={i} className={styles.originRow}>
              <Select value={o.protocol} onValueChange={(v) => setOrigin(i, { protocol: v })}>
                <SelectTrigger className={styles.protocolSelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROTOCOLS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="origin.example.com or 1.2.3.4"
                value={o.origin}
                onChange={(e) => setOrigin(i, { origin: e.target.value })}
              />
              <Input
                type="number"
                min={1}
                placeholder="1"
                title="Traffic weight — a higher number receives more traffic"
                value={o.weight}
                onChange={(e) => setOrigin(i, { weight: Number(e.target.value) })}
              />
              <IconButton
                aria-label="Remove origin"
                variant="ghost"
                disabled={form.origins.length <= 1}
                onClick={() => removeOrigin(i)}
              >
                <Trash2 size={16} />
              </IconButton>
            </div>
          ))}
        </CardBody>
      </Card>

      {/* Load Balancing between origins — mirrors the vendor's section: always
          rendered, with the "(if there are multiple origin)" caveat in the title
          (it only takes effect when the website has 2+ origins). load_balance +
          enable_failover persist to the /site upstream object. */}
      <Card>
        <CardHeader>
          <CardTitle>Load Balancing between Origins (if there are multiple origin)</CardTitle>
          <CardDescription>
            How traffic is spread across origins: IP Hash pins each client to one origin,
            Round Robin cycles evenly, Least Connections favors the least-busy origin. Only
            applies when the website has 2 or more origins.
          </CardDescription>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          <Select value={form.load_balance} onValueChange={(v) => set('load_balance', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LOAD_BALANCE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className={styles.checkLabel}>
            <Checkbox
              checked={form.enable_failover}
              onCheckedChange={(v) => set('enable_failover', Boolean(v))}
            />
            Enable Failover — route to the next origin when one is unhealthy.
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Keep-alive</CardTitle>
          <CardDescription>Reuse upstream TCP connections to improve origin performance.</CardDescription>
        </CardHeader>
        <CardBody>
          <label className={styles.checkLabel}>
            <Checkbox checked={form.keep_alive} onCheckedChange={(v) => set('keep_alive', Boolean(v))} />
            Enable
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Other</CardTitle>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          <label className={styles.checkLabel}>
            <Checkbox checked={form.http2} onCheckedChange={(v) => set('http2', Boolean(v))} />
            HTTP/2 — let clients connect to the edge over HTTP/2 (configure an SSL certificate first).
          </label>
          <label className={styles.checkLabel}>
            <Checkbox checked={form.redirect_http_to_https} onCheckedChange={(v) => set('redirect_http_to_https', Boolean(v))} />
            Redirect HTTP to HTTPS — force plain HTTP requests to HTTPS at the edge.
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          actions={
            <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addHeader}>
              Add Custom Header
            </Button>
          }
        >
          <CardTitle>HTTP Request Header Setting</CardTitle>
          <CardDescription>Add or replace headers in requests before forwarding to the origin.</CardDescription>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          {form.request_headers.map((h, i) => {
            // Row 0 is the fixed Host header (key locked, not deletable); the rest are custom.
            const isHost = i === 0;
            return (
              <div key={i} className={styles.originRow}>
                <Input
                  className={styles.grow}
                  placeholder="Header"
                  value={h.key}
                  disabled={isHost}
                  onChange={(e) => setHeader(i, { key: e.target.value })}
                />
                <Input
                  className={styles.grow}
                  placeholder={isHost ? '$http_host' : 'Value'}
                  value={h.value}
                  onChange={(e) => setHeader(i, { value: e.target.value })}
                />
                <label className={styles.checkLabel}>
                  <Checkbox checked={h.enabled} onCheckedChange={(v) => setHeader(i, { enabled: Boolean(v) })} />
                  Enable
                </label>
                <IconButton
                  aria-label={isHost ? 'Host header is required' : 'Remove header'}
                  variant="ghost"
                  disabled={isHost}
                  onClick={() => removeHeader(i)}
                >
                  <Trash2 size={16} />
                </IconButton>
              </div>
            );
          })}
        </CardBody>
      </Card>

      <div className={styles.footer}>
        <Button disabled={!validOrigins || update.isPending} onClick={save}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
